import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CreateTaskSheet } from '@/pages/tasks/CreateTaskSheet';
import { Plus, Loader2, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

// Mock data for simulation
const MOCK_SPACES = [
  { id: '1', name: 'Site Alpha' },
  { id: '2', name: 'Main Office' },
  { id: '3', name: 'Warehouse B' },
  { id: '4', name: 'Distribution Center' },
];

export function CreateTaskTile({ filter, size, useMockData }) {
  const { userProfile, company } = useAuth();
  const [spaces, setSpaces] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState(filter?.spaceId || '');
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    if (useMockData) {
      setSpaces(MOCK_SPACES);
      if (!selectedSpace) {
        setSelectedSpace(MOCK_SPACES[0].id);
      }
      setLoading(false);
      return;
    }

    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadSpaces = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('projects')
          .select('id, name')
          .eq('company_id', company.id)
          .eq('is_archived', false)
          .order('name');

        if (error) throw error;

        setSpaces(data || []);
        if (data?.length > 0 && !selectedSpace) {
          setSelectedSpace(data[0].id);
        }
      } catch (err) {
        console.error('Failed to load spaces:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSpaces();
  }, [company?.id, useMockData]);

  // Update selected space when filter changes
  useEffect(() => {
    if (filter?.spaceId) {
      setSelectedSpace(filter.spaceId);
    }
  }, [filter?.spaceId]);

  const handleCreateTask = async (taskData) => {
    if (!selectedSpace || !userProfile?.id) return;

    try {
      const { error } = await supabase
        .from('tasks')
        .insert({
          title: taskData.name,
          description: taskData.description || null,
          project_id: selectedSpace,
          created_by: userProfile.id,
          assigned_to: taskData.assignedTo || [userProfile.id],
          status: 'todo',
          due_date: taskData.dueDate || null,
          priority: taskData.priority || null,
          subtasks: taskData.subtasks || [],
          attachments: taskData.attachments || [],
          recurrence_type: taskData.recurrence_type || null,
          recurrence_interval: taskData.recurrence_interval || null,
        });

      if (error) throw error;
      toast.success('Task created successfully');
    } catch (err) {
      console.error('Failed to create task:', err);
      toast.error('Failed to create task');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (spaces.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-gray-100 p-3 rounded-full mb-3">
          <FolderOpen className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm text-gray-600">No spaces available</p>
      </div>
    );
  }

  const selectedSpaceName = spaces.find(s => s.id === selectedSpace)?.name || 'Select space';

  return (
    <div className="h-full flex flex-col p-1">
      <div className="flex-1 flex flex-col items-center justify-center gap-3">
        <Select value={selectedSpace} onValueChange={setSelectedSpace}>
          <SelectTrigger className="w-full max-w-[200px]">
            <SelectValue placeholder="Select space" />
          </SelectTrigger>
          <SelectContent>
            {spaces.map((space) => (
              <SelectItem key={space.id} value={space.id}>
                {space.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          onClick={() => setSheetOpen(true)}
          disabled={!selectedSpace}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Task
        </Button>
      </div>

      <CreateTaskSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onCreateTask={handleCreateTask}
        projectId={selectedSpace}
      />
    </div>
  );
}
